import Transition from './Transition';
import Node from './Node';

export default class DirectedAcyclicGraph {
	constructor(
		nodes = [],
		transitions = [],
		options = {
			quiet: false,
			cyclic: false,
		}
	) {
		this.graph = {};
		this.visited = {};
		this.startNode = null;
		this.options = options;

		this.populateGraph(nodes);
		this.populateTransitions(transitions);
		this.resetVisited();
	}

	json() {
		return JSON.parse(JSON.stringify(this));
	}

	jsonGraph() {
		return JSON.parse(JSON.stringify(this.graph));
	}

	resetVisited() {
		this.visited = Object.keys(this.graph).reduce((acc, step) => {
			return {
				...acc,
				[step]: false,
			};
		}, {});
	}

	getLatestNode(state, startNode) {
		let lastVisitedNode = startNode || this.startNode;
		const stack = [lastVisitedNode];
		this.resetVisited();

		// Perform topological-ordered DFS.
		while (stack.length) {
			const currentNode = stack.pop();
			lastVisitedNode = currentNode;

			const transitions = this.graph[currentNode].transitions;
			let foundTransition = false;

			for (let i = 0; i < transitions.length && !foundTransition; i++) {
				const transition = transitions[i];

				if (transition.requirement(state)) {
					this.visited[transition.startNode] = true;
					stack.push(transition.nextNode);
					foundTransition = true;
				}
			}

			if (!foundTransition) {
				return currentNode;
			}
		}

		return lastVisitedNode;
	}

	getNextNode(state, currentNode = this.startNode) {
		const transitions = this.graph[currentNode].transitions;

		for (let i = 0; i < transitions.length; i++) {
			const transition = transitions[i];

			if (transition.requirement(state)) {
				return transition.nextNode;
			}
		}

		return currentNode;
	}

	addNode(nodeName) {
		// Add node if it's name hasn't been taken.
		if (!this.graph[nodeName]) {
			this.graph[nodeName] = new Node(nodeName);
		} else {
			this._nodeAlreadyExistError(nodeName);
		}

		// The first node added will be the start node by default.
		if (!this.startNode) {
			this.startNode = nodeName;
		}
	}

	setStartNode(nodeName) {
		if (this.graph[nodeName]) {
			this.startNode = nodeName;
		} else {
			this._error(
				`DirectedAcyclicGraph StartError\nCannot set "${nodeName}" as the start node.`
			);
		}
	}

	addTransition(startNode, requirement, nextNode) {
		const transition = new Transition(startNode, nextNode, requirement);
		const { cyclic } = this.options;

		if (!this.graph[startNode]) {
			return this._nodeDoesNotExistError(startNode, transition.name);
		}

		if (!this.graph[nextNode]) {
			return this._nodeDoesNotExistError(nextNode, transition.name);
		}

		if (cyclic || this._remainsAcyclic(transition)) {
			this.graph[transition.startNode].addTransition(transition);
		} else {
			return this._error(
				`DirectedAcyclicGraph CycleError\nTransition creates cycle ${transition.name}`
			);
		}
	}

	populateGraph(nodeNames) {
		nodeNames.forEach((nodeName) => {
			this.addNode(nodeName);
		});
	}

	populateTransitions(transitions) {
		Object.keys(transitions).forEach((startNode) => {
			const neighbors = transitions[startNode];
			neighbors.forEach((neighbor) => {
				this.addTransition(startNode, ...neighbor);
			});
		});
	}

	_createObjectSet(array, defaultValue = true) {
		return array.reduce((acc, keyName) => {
			return {
				...acc,
				[keyName]: defaultValue,
			};
		}, {});
	}

	// Detects if the given graph has a cycle.
	// Algorithm: https://youtu.be/rKQaZuoUR4M
	_isAcyclic(graph = this.graph) {
		const unvisitedSet = this._createObjectSet(Object.keys(graph));
		const visitingSet = {};
		const visitedSet = {};

		const moveStep = (node, sourceSet, targetSet) => {
			delete sourceSet[node];
			targetSet[node] = true;
		};

		const findCycleDFS = (node) => {
			moveStep(node, unvisitedSet, visitingSet);
			const neighbors = graph[node].transitions;

			for (let i = 0; i < neighbors.length; i++) {
				const neighbor = neighbors[i];

				// If the node has been completely visited, then ignore it.
				if (visitedSet[neighbor.nextNode]) {
					continue;
				}

				// If the node is found while visiting, then it has found a cycle.
				if (visitingSet[neighbor.nextNode]) {
					return true;
				}

				// Recursively check neighbors for cycles with DFS.
				if (findCycleDFS(neighbor.nextNode)) {
					return true;
				}
			}

			moveStep(node, visitingSet, visitedSet);
			return false;
		};

		while (Object.keys(unvisitedSet).length > 0) {
			const currentStep = Object.keys(unvisitedSet)[0];

			if (findCycleDFS(currentStep)) {
				return false;
			}
		}

		return true;
	}

	// Checks to see if graph remains acyclic with new transition.
	_remainsAcyclic(transition) {
		const newTransitions = [...this.graph[transition.startNode].transitions, transition];

		const newGraph = {
			...this.graph,
			[transition.startNode]: {
				...this.graph[transition.startNode],
				transitions: newTransitions,
			},
		};

		return this._isAcyclic(newGraph);
	}

	_error(message) {
		if (!this.options.quiet) {
			throw new Error(message);
		}
	}

	_nodeDoesNotExistError(node, transitionName) {
		return this._error(
			`DirectedAcyclicGraph NodeError\nNode "${node}" does not exist in transition "${transitionName}"`
		);
	}

	_nodeAlreadyExistError(node) {
		return this._error(`DirectedAcyclicGraph NodeError\nNode "${node}" already exists'`);
	}
}
